/**
 * Post-answer feedback for the einbuergerungstest-vokabeln app.
 *
 * Shows, in order: the verdict, the correct gloss, the compound/prefix split,
 * a false-friend warning when there is one, and the exam sentence the word
 * appears in.
 *
 * The wrong options are deliberately NOT repeated here. On a three-option
 * recognition card the useful information is what the word *does* mean; the
 * two glosses that don't apply are noise, and on a phone they would push the
 * split and the example sentence below the fold.
 *
 * The example sentence is a real BAMF question — the seed loader picks the
 * first question whose stem contains the word, so this is literally a sentence
 * from the exam rather than an invented illustration.
 */
import { Check, Quote, TriangleAlert, X } from 'lucide-react';

import type { VokabelnPayload } from '@/core/types';
import { cn } from '@/lib/utils';

import type { CardBackProps } from '../types';
import { correctGloss, genderBorder, genderText } from './card-utils';

export function CardBack({ card, correct, isLastCard, onNext }: CardBackProps<VokabelnPayload>) {
  const { article, parts, question_ids, example_question, example_answer, example_source } =
    card.payload;
  const example = card.example;
  const exampleEn = card.example_translations?.en;
  const exampleQuestion = example_question;
  const exampleAnswer = example_answer;
  // The word appears only in a distractor, so the displayed example is a
  // sentence the exam marks false. It must not sit next to the correct answer
  // unlabelled.
  const isWrongOptionExample = example_source === 'option';

  return (
    <div
      className={cn(
        'border-border bg-card flex w-full flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8',
        'border-l-4',
        genderBorder(article),
      )}
    >
      {/* Verdict + the word. */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-display text-3xl font-semibold sm:text-4xl">
            {article ? (
              <>
                <span className={genderText(article)}>{article} </span>
                {card.de}
              </>
            ) : (
              card.de
            )}
          </div>
          <span
            className={cn(
              'flex shrink-0 items-center gap-1.5 text-sm font-medium',
              correct ? 'text-success' : 'text-destructive',
            )}
          >
            {correct ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
            {correct ? 'Correct' : 'Incorrect'}
          </span>
        </div>

        <div className="text-foreground text-lg">{correctGloss(card)}</div>

        {card.payload.plural && (
          <div className="text-muted-foreground text-sm">{card.payload.plural}</div>
        )}
      </div>

      {/* Compound / separable-prefix split. Present only when every part is a
          standalone word whose meaning contributes — the seed refuses to
          invent a split otherwise. */}
      {parts && parts.length > 0 && (
        <div className="bg-muted/50 flex flex-col gap-2 rounded-lg p-4">
          <div className="font-display flex flex-wrap items-baseline gap-x-2 gap-y-1 text-lg">
            {parts.map((part, i) => (
              <span key={`${part.surface}-${i}`} className="flex items-baseline gap-2">
                {i > 0 && <span className="text-muted-foreground">+</span>}
                <span>{part.surface}</span>
              </span>
            ))}
          </div>
          <div className="text-muted-foreground flex flex-col gap-0.5 text-sm">
            {parts.map((part, i) => (
              <div key={`${part.lemma}-${i}`}>
                <span className="text-foreground font-medium">{part.lemma}</span> — {part.en}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* False friend. The one thing most likely to make a question misread. */}
      {card.warning && (
        <div className="border-warning/40 bg-warning/10 flex items-start gap-2 rounded-lg border p-3 text-sm">
          <TriangleAlert className="text-warning mt-0.5 h-4 w-4 shrink-0" />
          <span>{card.warning}</span>
        </div>
      )}

      {/* The exam question this word came from, with its answer — a question
          without its answer is only half a flashcard, and the civic content is
          worth learning alongside the vocabulary. */}
      {exampleQuestion ? (
        <div className="border-border flex flex-col gap-3 rounded-lg border p-4 text-sm">
          <div className="flex items-start gap-2">
            <Quote className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="text-foreground">{exampleQuestion.de}</span>
              {exampleQuestion.en && (
                <span className="text-muted-foreground">{exampleQuestion.en}</span>
              )}
            </div>
          </div>

          {/* Only when the word appears in a *wrong* option. Kept visually
              apart from the answer below, and labelled, so it can't be read as
              the correct one — the exam marks this sentence false. */}
          {isWrongOptionExample && example && (
            <div className="border-border/70 flex flex-col gap-1 border-l-2 pl-3">
              <span className="text-muted-foreground text-xs">
                “{card.de}” appears in another option:
              </span>
              <span className="text-foreground">{example}</span>
              {exampleEn && <span className="text-muted-foreground">{exampleEn}</span>}
            </div>
          )}

          {exampleAnswer && (
            <div className="flex items-start gap-2">
              <Check className="text-success mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex flex-col gap-1">
                <span className="text-foreground font-medium">{exampleAnswer.de}</span>
                {exampleAnswer.en && (
                  <span className="text-muted-foreground">{exampleAnswer.en}</span>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Rows seeded before the Q&A existed still have a bare example sentence.
        example && (
          <div className="flex items-start gap-2 text-sm">
            <Quote className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="text-foreground">{example}</span>
              {exampleEn && <span className="text-muted-foreground">{exampleEn}</span>}
            </div>
          </div>
        )
      )}

      <div className="text-muted-foreground text-xs">{coverageLabel(question_ids.length)}</div>

      <button
        type="button"
        onClick={onNext}
        autoFocus
        className={cn(
          'bg-primary text-primary-foreground min-h-12 w-full rounded-lg px-4 py-3 font-medium transition-colors',
          'hover:bg-primary/90 focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        )}
      >
        {isLastCard ? 'Finish session' : 'Next word →'}
      </button>
    </div>
  );
}

/** "appears in 7 exam questions" — the reason this word is worth the time. */
function coverageLabel(count: number): string {
  if (count === 0) return 'Not linked to an exam question';
  if (count === 1) return 'Appears in 1 exam question';
  return `Appears in ${count} exam questions`;
}
